import React, { useState, useEffect } from 'react';
import { GlassCard, GlassCardContent } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { UppercaseInput } from './UppercaseInput';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Textarea } from '@easyflow/ui-master/components/ui/textarea';
import { useLegalForms } from '@easyflow/ui-frontend';
import { PRODUCT_REGISTRY, DEFAULT_PRODUCT, type TenantProduct } from '@easyflow/shared';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@easyflow/ui-master/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import {
  Building2,
  User,
  FileText,
  Check,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  UserCheck,
  Sparkles
} from 'lucide-react';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import AddressAutocomplete from '@easyflow/ui-frontend/components/AddressAutocomplete';
import { masterAddressFetch } from '@/lib/masterAddressFetch';
import { buildDefaultSipDomain } from '@easyflow/shared';
import { useProductOrigins } from '@/lib/productOrigins';

function generateStrongPassword(length = 16): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%^&*?-_=+';
  const all = upper + lower + digits + symbols;
  const cryptoRandom = (max: number): number => {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const buf = new Uint32Array(1);
      window.crypto.getRandomValues(buf);
      return (buf[0] ?? 0) % max;
    }
    return Math.floor(Math.random() * max);
  };
  const pick = (set: string): string => set.charAt(cryptoRandom(set.length));
  const chars: string[] = [pick(upper), pick(lower), pick(digits), pick(symbols)];
  for (let i = chars.length; i < length; i++) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = cryptoRandom(i + 1);
    const ci = chars[i] ?? '';
    const cj = chars[j] ?? '';
    chars[i] = cj;
    chars[j] = ci;
  }
  return chars.join('');
}

interface CompanyInfo {
  legal_name: string;
  legal_form: string;
  vat_number: string;
  fiscal_code: string;
  address: string;
  city: string;
  postal_code: string;
  province: string;
  country: string;
  pec: string;
  phone: string;
  description: string;
}

interface AdminUser {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  role: 'admin';
}

interface TenantBasicInfo {
  name: string;
  slug: string;
  sip_domain: string;
  status: 'active' | 'pending' | 'suspended';
  language: string;
  timezone: string;
  // Task #3148 — prodotto/edition (catalogo da PRODUCT_REGISTRY shared).
  product: TenantProduct;
}

interface TenantWizardData {
  basic: TenantBasicInfo;
  company: CompanyInfo;
  admin: AdminUser;
}

interface WonLead {
  id: string;
  contract_number?: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  company_name: string;
  vat_number: string;
  fiscal_code: string;
  sdi_code: string;
  pec: string;
  legal_address: string;
  legal_city: string;
  legal_province: string;
  legal_zip: string;
  contact_first_name: string;
  contact_last_name: string;
}


interface CrmQuoteSummary {
  id: string;
  quote_number: string;
  total_gross: number | null;
}

interface CrmContractSummary {
  id: string;
  contract_number: string;
  status: string;
}

interface CrmSummary {
  accepted_quotes?: CrmQuoteSummary[];
  active_contracts?: CrmContractSummary[];
  payment_method?: { type: string; brand?: string; last4?: string } | null;
}

interface TenantWizardProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TenantWizardData, linkedLeadId?: string) => Promise<void>;
  editMode?: boolean;
  initialData?: Partial<TenantWizardData>;
  isSubmitting?: boolean;
}

const TIMEZONES = [
  { value: 'Europe/Rome', label: 'Europe/Rome (CET)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
  { value: 'America/New_York', label: 'America/New_York (EST)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
];

const LANGUAGES = [
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
];

const STEPS = [
  { id: 1, title: 'Informazioni Tenant', icon: Building2, description: 'Nome, slug e configurazione base' },
  { id: 2, title: 'Ragione Sociale', icon: FileText, description: 'Dati aziendali e fiscali' },
  { id: 3, title: 'Admin Iniziale', icon: User, description: 'Primo utente amministratore' },
];

const initialBasic: TenantBasicInfo = {
  name: '',
  slug: '',
  sip_domain: '',
  status: 'active',
  language: 'it',
  timezone: 'Europe/Rome',
  product: DEFAULT_PRODUCT,
};

const initialCompany: CompanyInfo = {
  legal_name: '',
  legal_form: '',
  vat_number: '',
  fiscal_code: '',
  address: '',
  city: '',
  postal_code: '',
  province: '',
  country: 'Italia',
  pec: '',
  phone: '',
  description: '',
};

const initialAdmin: AdminUser = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  password: '',
  role: 'admin',
};

export default function TenantWizard({
  open,
  onClose,
  onSubmit,
  editMode = false,
  initialData,
  isSubmitting = false
}: TenantWizardProps) {
  const { toast } = useToast();
  // Task #3260 — origin del tenant product-aware (fallback dominio piattaforma).
  const tenantOriginFor = useProductOrigins();
  const { legalForms } = useLegalForms(open);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [basic, setBasic] = useState<TenantBasicInfo>(initialBasic);
  const [company, setCompany] = useState<CompanyInfo>(initialCompany);
  const [admin, setAdmin] = useState<AdminUser>(initialAdmin);
  const [wonLeads, setWonLeads] = useState<WonLead[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [crmSummary, setCrmSummary] = useState<CrmSummary | null>(null);
  const [loadingCrmSummary, setLoadingCrmSummary] = useState(false);

  useEffect(() => {
    if (initialData) {
      if (initialData.basic) setBasic({ ...initialBasic, ...initialData.basic });
      if (initialData.company) setCompany({ ...initialCompany, ...initialData.company });
      if (initialData.admin) setAdmin({ ...initialAdmin, ...initialData.admin });
    }
  }, [initialData]);

  useEffect(() => {
    if (open && !editMode) {
      apiClient.get<WonLead[]>('/master/tenants/linkable-leads').then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setWonLeads(res.data);
        }
      }).catch(() => {});
    }
  }, [open, editMode]);

  useEffect(() => {
    if (!open) {
      setCurrentStep(1);
      setErrors({});
      setSelectedLeadId('');
      setCrmSummary(null);
      if (!editMode) {
        setBasic(initialBasic);
        setCompany(initialCompany);
        setAdmin(initialAdmin);
      }
    }
  }, [open, editMode]);

  const handleLeadSelect = (leadId: string) => {
    setSelectedLeadId(leadId);
    setCrmSummary(null);
    if (leadId === '') return;
    const lead = wonLeads.find(l => l.id === leadId);
    if (!lead) return;

    setLoadingCrmSummary(true);
    apiClient.get<CrmSummary>(`/master/crm/pipeline/entries/${leadId}/crm-summary`).then((res) => {
      if (res.success && res.data) setCrmSummary(res.data);
    }).catch(() => {}).finally(() => setLoadingCrmSummary(false));
    const name = lead.company_name || lead.contact_name || '';
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setBasic(prev => ({
      ...prev,
      name: name || prev.name,
      slug: slug || prev.slug,
    }));
    setCompany(prev => ({
      ...prev,
      legal_name: lead.company_name || prev.legal_name,
      vat_number: lead.vat_number || prev.vat_number,
      fiscal_code: lead.fiscal_code || prev.fiscal_code,
      pec: lead.pec || prev.pec,
      address: lead.legal_address || prev.address,
      city: lead.legal_city || prev.city,
      province: lead.legal_province || prev.province,
      postal_code: lead.legal_zip || prev.postal_code,
    }));
    const firstName = lead.contact_first_name || (lead.contact_name || '').split(' ')[0] || '';
    const lastName = lead.contact_last_name || (lead.contact_name || '').split(' ').slice(1).join(' ') || '';
    setAdmin(prev => ({
      ...prev,
      firstName: firstName || prev.firstName,
      lastName: lastName || prev.lastName,
      email: lead.contact_email || prev.email,
    }));
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (value: string) => {
    setBasic(prev => ({
      ...prev,
      name: value,
      slug: prev.slug || generateSlug(value)
    }));
  };

  const handleSlugChange = (value: string) => {
    const slug = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setBasic(prev => ({ ...prev, slug }));
    
    if (slug && !/^[a-z0-9-]+$/.test(slug)) {
      setErrors(prev => ({ ...prev, slug: 'Solo lettere minuscole, numeri e trattini' }));
    } else {
      setErrors(prev => {
        const { slug: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!basic.name.trim()) newErrors.name = 'Nome tenant obbligatorio';
      if (!basic.slug.trim()) newErrors.slug = 'Slug obbligatorio';
      if (basic.slug && !/^[a-z0-9-]+$/.test(basic.slug)) {
        newErrors.slug = 'Solo lettere minuscole, numeri e trattini';
      }
    }

    if (step === 2) {
      if (!company.legal_name.trim()) newErrors.legal_name = 'Ragione sociale obbligatoria';
      // legal_form, vat_number, city, address are optional - don't block wizard
    }

    if (step === 3 && !editMode) {
      if (!admin.firstName.trim()) newErrors.firstName = 'Nome obbligatorio';
      if (!admin.lastName.trim()) newErrors.lastName = 'Cognome obbligatorio';
      if (!admin.email.trim()) newErrors.email = 'Email obbligatoria';
      if (admin.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(admin.email)) {
        newErrors.email = 'Email non valida';
      }
      if (!admin.password.trim()) newErrors.password = 'Password obbligatoria';
      if (admin.password && admin.password.length < 8) {
        newErrors.password = 'Minimo 8 caratteri';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    await onSubmit({
      basic,
      company,
      admin
    }, selectedLeadId || undefined);
  };

  const sipDomain = basic.slug ? buildDefaultSipDomain(basic.slug) : '';
  // Task #3260 — login URL sul dominio del prodotto selezionato (fallback piattaforma).
  const loginUrl = basic.slug ? `${tenantOriginFor(basic.product)}/${basic.slug}/login` : '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col overflow-hidden p-0 bg-white dark:bg-slate-900" aria-describedby="tenant-wizard-description">
        <DialogTitle className="sr-only">
          {editMode ? 'Modifica Tenant' : 'Crea Nuovo Tenant'}
        </DialogTitle>
        <DialogDescription id="tenant-wizard-description" className="sr-only">
          {editMode ? 'Modifica le informazioni del tenant' : 'Configura un nuovo tenant in 3 semplici passaggi'}
        </DialogDescription>
        <div className="flex flex-col h-full">
          {/* Header with Stepper */}
          <div className="px-6 pt-6 pb-4 border-b border-border dark:border-white/10">
            <h2 className="text-xl font-bold text-foreground mb-1">
              {editMode ? 'Modifica Tenant' : 'Crea Nuovo Tenant'}
            </h2>
            <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">
              {editMode ? 'Modifica le informazioni del tenant' : 'Configura un nuovo tenant in 3 semplici passaggi'}
            </p>

            {/* Stepper */}
            <div className="flex items-center justify-between mt-6">
              {STEPS.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;

                return (
                  <React.Fragment key={step.id}>
                    <div className="flex items-center">
                      <div
                        className={`
                          flex items-center justify-center w-10 h-10 rounded-full transition-all
                          ${isCompleted 
                            ? 'bg-emerald-500 text-white' 
                            : isActive 
                              ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-lg' 
                              : 'bg-muted dark:bg-white/10 text-muted-foreground/70 dark:text-muted-foreground'}
                        `}
                      >
                        {isCompleted ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <StepIcon className="w-5 h-5" />
                        )}
                      </div>
                      <div className="ml-3 hidden sm:block">
                        <p className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground dark:text-muted-foreground/70'}`}>
                          {step.title}
                        </p>
                        <p className="text-xs text-muted-foreground/70 dark:text-muted-foreground">{step.description}</p>
                      </div>
                    </div>
                    {index < STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-4 ${isCompleted ? 'bg-emerald-500' : 'bg-muted dark:bg-white/10'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto p-6">
            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="space-y-6">
                {!editMode && wonLeads.length > 0 && (
                  <GlassCard className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-0">
                    <div className="flex items-center gap-3 mb-4">
                      <UserCheck className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">Collega a Contratto Finalizzato</h3>
                        <p className="text-xs text-muted-foreground dark:text-muted-foreground/70">Seleziona un contratto finalizzato per pre-compilare il form</p>
                      </div>
                    </div>
                    <Select value={selectedLeadId} onValueChange={handleLeadSelect}>
                      <SelectTrigger className="bg-white dark:bg-muted">
                        <SelectValue placeholder="Seleziona un contratto finalizzato (opzionale)..." />
                      </SelectTrigger>
                      <SelectContent>
                        {wonLeads.map(lead => (
                          <SelectItem key={lead.id} value={lead.id}>
                            {lead.company_name || lead.contact_name}{lead.contract_number ? ` — Contr. ${lead.contract_number}` : ''}{lead.vat_number ? ` — P.IVA: ${lead.vat_number}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {loadingCrmSummary && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Caricamento dati CRM...
                      </div>
                    )}

                    {crmSummary && !loadingCrmSummary && (
                      <div className="mt-4 space-y-3">
                        {crmSummary.accepted_quotes && crmSummary.accepted_quotes.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-foreground dark:text-muted-foreground/60 mb-1">Preventivi Accettati</h4>
                            <div className="space-y-1">
                              {crmSummary.accepted_quotes.map((q) => (
                                <div key={q.id} className="flex justify-between text-sm bg-white/60 dark:bg-muted/60 rounded px-3 py-1.5">
                                  <span className="text-muted-foreground dark:text-muted-foreground/70">{q.quote_number}</span>
                                  <span className="font-medium text-foreground">€ {(q.total_gross ?? 0).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {crmSummary.active_contracts && crmSummary.active_contracts.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-foreground dark:text-muted-foreground/60 mb-1">Contratti Attivi</h4>
                            <div className="space-y-1">
                              {crmSummary.active_contracts.map((c) => (
                                <div key={c.id} className="flex justify-between text-sm bg-white/60 dark:bg-muted/60 rounded px-3 py-1.5">
                                  <span className="text-muted-foreground dark:text-muted-foreground/70">{c.contract_number}</span>
                                  <Badge variant="outline" className="text-green-600">{c.status}</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {crmSummary.payment_method && (
                          <div>
                            <h4 className="text-sm font-semibold text-foreground dark:text-muted-foreground/60 mb-1">Metodo di Pagamento</h4>
                            <div className="text-sm bg-white/60 dark:bg-muted/60 rounded px-3 py-1.5 text-muted-foreground dark:text-muted-foreground/70">
                              {crmSummary.payment_method.type === 'card'
                                ? `Carta ${(crmSummary.payment_method.brand || '').charAt(0).toUpperCase() + (crmSummary.payment_method.brand || '').slice(1)}`
                                : crmSummary.payment_method.type === 'sepa_debit' ? 'SEPA' : crmSummary.payment_method.type}
                              {' '}**** {crmSummary.payment_method.last4}
                            </div>
                          </div>
                        )}
                        {!crmSummary.accepted_quotes?.length && !crmSummary.active_contracts?.length && !crmSummary.payment_method && (
                          <p className="text-sm text-muted-foreground dark:text-muted-foreground/70 italic">Nessun dato CRM disponibile per questo lead.</p>
                        )}
                      </div>
                    )}
                  </GlassCard>
                )}

                <GlassCard className="p-5 bg-gradient-to-br from-cyan-50 to-purple-50 dark:from-cyan-900/20 dark:to-purple-900/20 border-0">
                  <div className="flex items-center gap-3 mb-4">
                    <Building2 className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                    <h3 className="text-lg font-semibold text-foreground">Informazioni Base</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-foreground dark:text-muted-foreground/60">
                        Nome Tenant *
                      </Label>
                      <UppercaseInput
                        id="name"
                        value={basic.name}
                        onChange={(v) => handleNameChange(v)}
                        placeholder="Es. Acme Corporation"
                        className={`bg-white dark:bg-muted ${errors.name ? 'border-red-500' : ''}`}
                      />
                      {errors.name && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="slug" className="text-foreground dark:text-muted-foreground/60">
                        Slug * <span className="text-xs text-muted-foreground/70">(solo minuscole, numeri, trattini)</span>
                      </Label>
                      <Input
                        id="slug"
                        value={basic.slug}
                        onChange={(e) => handleSlugChange(e.target.value)}
                        placeholder="acme"
                        className={`bg-white dark:bg-muted font-mono ${errors.slug ? 'border-red-500' : ''}`}
                      />
                      {errors.slug && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.slug}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="language" className="text-foreground dark:text-muted-foreground/60">Lingua</Label>
                      <Select value={basic.language} onValueChange={(v) => setBasic(prev => ({ ...prev, language: v }))}>
                        <SelectTrigger className="bg-white dark:bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LANGUAGES.map(lang => (
                            <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="timezone" className="text-foreground dark:text-muted-foreground/60">Timezone</Label>
                      <Select value={basic.timezone} onValueChange={(v) => setBasic(prev => ({ ...prev, timezone: v }))}>
                        <SelectTrigger className="bg-white dark:bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map(tz => (
                            <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="status" className="text-foreground dark:text-muted-foreground/60">Stato</Label>
                      <Select value={basic.status} onValueChange={(v) => setBasic(prev => ({ ...prev, status: v as TenantBasicInfo['status'] }))}>
                        <SelectTrigger className="bg-white dark:bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Attivo</SelectItem>
                          <SelectItem value="pending">In attesa</SelectItem>
                          <SelectItem value="suspended">Sospeso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="product" className="text-foreground dark:text-muted-foreground/60">Prodotto</Label>
                      <Select
                        value={basic.product}
                        onValueChange={(v) => setBasic(prev => ({ ...prev, product: v as TenantProduct }))}
                        disabled={editMode}
                      >
                        <SelectTrigger className="bg-white dark:bg-muted">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRODUCT_REGISTRY.map(p => (
                            <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* URL Preview */}
                  {basic.slug && !errors.slug && (
                    <div className="mt-6 p-4 rounded-lg bg-white/80 dark:bg-muted/50 border border-border dark:border-white/10">
                      <p className="text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-3">Anteprima URL:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-400">
                            Login
                          </Badge>
                          <code className="text-sm bg-muted px-2 py-1 rounded">{loginUrl}</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400">
                            SIP
                          </Badge>
                          <code className="text-sm bg-muted px-2 py-1 rounded">{sipDomain}</code>
                        </div>
                      </div>
                    </div>
                  )}
                </GlassCard>
              </div>
            )}

            {/* Step 2: Company Info */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <GlassCard className="p-5 bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-900/20 dark:to-cyan-900/20 border-0">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    <h3 className="text-lg font-semibold text-foreground">Dati Aziendali</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="legal_name" className="text-foreground dark:text-muted-foreground/60">
                        Ragione Sociale *
                      </Label>
                      <UppercaseInput
                        id="legal_name"
                        value={company.legal_name}
                        onChange={(v) => setCompany(prev => ({ ...prev, legal_name: v }))}
                        placeholder="Es. Acme S.r.l."
                        className={`bg-white dark:bg-muted ${errors.legal_name ? 'border-red-500' : ''}`}
                      />
                      {errors.legal_name && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.legal_name}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="legal_form" className="text-foreground dark:text-muted-foreground/60">Forma Giuridica</Label>
                      <Select value={company.legal_form} onValueChange={(v) => setCompany(prev => ({ ...prev, legal_form: v }))}>
                        <SelectTrigger className="bg-white dark:bg-muted">
                          <SelectValue placeholder="Seleziona..." />
                        </SelectTrigger>
                        <SelectContent>
                          {legalForms.map(form => (
                            <SelectItem key={form.code} value={form.code}>{form.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vat_number" className="text-foreground dark:text-muted-foreground/60">Partita IVA</Label>
                      <Input
                        id="vat_number"
                        value={company.vat_number}
                        onChange={(e) => setCompany(prev => ({ ...prev, vat_number: e.target.value }))}
                        placeholder="IT12345678901"
                        className="bg-white dark:bg-muted font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="fiscal_code" className="text-foreground dark:text-muted-foreground/60">Codice Fiscale</Label>
                      <Input
                        id="fiscal_code"
                        value={company.fiscal_code}
                        onChange={(e) => setCompany(prev => ({ ...prev, fiscal_code: e.target.value.toUpperCase() }))}
                        placeholder="12345678901"
                        className="bg-white dark:bg-muted font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="pec" className="text-foreground dark:text-muted-foreground/60">PEC</Label>
                      <Input
                        id="pec"
                        type="email"
                        value={company.pec}
                        onChange={(e) => setCompany(prev => ({ ...prev, pec: e.target.value }))}
                        placeholder="azienda@pec.it"
                        className="bg-white dark:bg-muted"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address" className="text-foreground dark:text-muted-foreground/60">Indirizzo</Label>
                      <AddressAutocomplete
                        defaultValue={company.address}
                        placeholder="Via Roma 123"
                        fetchSuggestions={masterAddressFetch}
                        onAddressSelect={(a) => setCompany(prev => ({
                          ...prev,
                          address: a.address ?? '',
                          ...(a.city !== undefined ? { city: a.city } : {}),
                          ...(a.province !== undefined ? { province: a.province } : {}),
                          ...(a.postal_code !== undefined ? { postal_code: a.postal_code } : {}),
                        }))}
                      />
                      <Input
                        id="address"
                        value={company.address}
                        onChange={(e) => setCompany(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="Via Roma 123"
                        className="bg-white dark:bg-muted"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-foreground dark:text-muted-foreground/60">Città</Label>
                      <Input
                        id="city"
                        value={company.city}
                        onChange={(e) => setCompany(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Roma"
                        className="bg-white dark:bg-muted"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="postal_code" className="text-foreground dark:text-muted-foreground/60">CAP</Label>
                        <Input
                          id="postal_code"
                          value={company.postal_code}
                          onChange={(e) => setCompany(prev => ({ ...prev, postal_code: e.target.value }))}
                          placeholder="00100"
                          className="bg-white dark:bg-muted"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="province" className="text-foreground dark:text-muted-foreground/60">Provincia</Label>
                        <Input
                          id="province"
                          value={company.province}
                          onChange={(e) => setCompany(prev => ({ ...prev, province: e.target.value.toUpperCase() }))}
                          placeholder="RM"
                          maxLength={2}
                          className="bg-white dark:bg-muted"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-foreground dark:text-muted-foreground/60">Telefono</Label>
                      <Input
                        id="phone"
                        value={company.phone}
                        onChange={(e) => setCompany(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+39 06 12345678"
                        className="bg-white dark:bg-muted"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="description" className="text-foreground dark:text-muted-foreground/60">Descrizione Attività</Label>
                      <Textarea
                        id="description"
                        value={company.description}
                        onChange={(e) => setCompany(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Breve descrizione dell'attività aziendale..."
                        rows={3}
                        className="bg-white dark:bg-muted resize-none"
                      />
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}

            {/* Step 3: Admin User */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <GlassCard className="p-5 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-0">
                  <div className="flex items-center gap-3 mb-4">
                    <User className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    <h3 className="text-lg font-semibold text-foreground">
                      {editMode ? 'Modifica Admin' : 'Primo Amministratore'}
                    </h3>
                  </div>

                  {editMode && (
                    <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        In modalità modifica, lascia i campi password vuoti per mantenere la password esistente.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-foreground dark:text-muted-foreground/60">Nome *</Label>
                      <UppercaseInput
                        id="firstName"
                        value={admin.firstName}
                        onChange={(v) => setAdmin(prev => ({ ...prev, firstName: v }))}
                        placeholder="Mario"
                        className={`bg-white dark:bg-muted ${errors.firstName ? 'border-red-500' : ''}`}
                      />
                      {errors.firstName && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.firstName}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-foreground dark:text-muted-foreground/60">Cognome *</Label>
                      <UppercaseInput
                        id="lastName"
                        value={admin.lastName}
                        onChange={(v) => setAdmin(prev => ({ ...prev, lastName: v }))}
                        placeholder="Rossi"
                        className={`bg-white dark:bg-muted ${errors.lastName ? 'border-red-500' : ''}`}
                      />
                      {errors.lastName && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.lastName}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-foreground dark:text-muted-foreground/60">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={admin.email}
                        onChange={(e) => setAdmin(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="admin@azienda.it"
                        className={`bg-white dark:bg-muted ${errors.email ? 'border-red-500' : ''}`}
                      />
                      {errors.email && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="username" className="text-foreground dark:text-muted-foreground/60">Username</Label>
                      <Input
                        id="username"
                        value={admin.username}
                        onChange={(e) => setAdmin(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="admin (opzionale)"
                        className="bg-white dark:bg-muted"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="password" className="text-foreground dark:text-muted-foreground/60">
                        Password {!editMode && '*'}
                      </Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={admin.password}
                            onChange={(e) => setAdmin(prev => ({ ...prev, password: e.target.value }))}
                            placeholder={editMode ? 'Lascia vuoto per non modificare' : 'Minimo 8 caratteri'}
                            className={`bg-white dark:bg-muted pr-10 ${errors.password ? 'border-red-500' : ''}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          title="Genera password sicura"
                          onClick={() => {
                            const pwd = generateStrongPassword(16);
                            setAdmin(prev => ({ ...prev, password: pwd }));
                            setShowPassword(true);
                            setErrors(prev => {
                              const { password, ...rest } = prev;
                              return rest;
                            });
                            toast({
                              title: 'Password generata',
                              description: 'Ricordati di salvarla prima di chiudere il wizard.',
                            });
                          }}
                          className="shrink-0 border-border dark:border-white/20 gap-1"
                        >
                          <Sparkles className="w-4 h-4" />
                          Genera
                        </Button>
                      </div>
                      {errors.password && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.password}
                        </p>
                      )}
                    </div>
                  </div>
                </GlassCard>

                {/* Summary */}
                <GlassCard className="p-5 bg-muted/50 dark:bg-muted/50">
                  <h4 className="font-medium text-foreground mb-3">Riepilogo</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground dark:text-muted-foreground/70">Tenant</p>
                      <p className="font-medium text-foreground">{basic.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground dark:text-muted-foreground/70">Ragione Sociale</p>
                      <p className="font-medium text-foreground">{company.legal_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground dark:text-muted-foreground/70">Admin</p>
                      <p className="font-medium text-foreground">
                        {admin.firstName || admin.lastName ? `${admin.firstName} ${admin.lastName}`.trim() : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground dark:text-muted-foreground/70">Login URL</p>
                      <p className="font-medium text-cyan-600 dark:text-cyan-400 font-mono text-xs">{loginUrl || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground dark:text-muted-foreground/70">P.IVA</p>
                      <p className="font-medium text-foreground font-mono">{company.vat_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground dark:text-muted-foreground/70">Email Admin</p>
                      <p className="font-medium text-foreground">{admin.email || '-'}</p>
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}
          </div>

          {/* Footer with Navigation */}
          <div className="px-6 py-4 border-t border-border dark:border-white/10 bg-muted/50 dark:bg-muted/50 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={currentStep === 1 ? onClose : handleBack}
              disabled={isSubmitting}
              className="border-border dark:border-white/20"
            >
              {currentStep === 1 ? 'Annulla' : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Indietro
                </>
              )}
            </Button>

            <div className="flex items-center gap-2">
              {STEPS.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentStep > idx + 1
                      ? 'bg-emerald-500'
                      : currentStep === idx + 1
                        ? 'bg-cyan-500 w-4'
                        : 'bg-slate-300 dark:bg-slate-600'
                  }`}
                />
              ))}
            </div>

            {currentStep < 3 ? (
              <Button
                onClick={handleNext}
                className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white"
              >
                Avanti
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editMode ? 'Salvando...' : 'Creando...'}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    {editMode ? 'Salva Modifiche' : 'Crea Tenant'}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export type { TenantWizardData, TenantBasicInfo, CompanyInfo, AdminUser };
